import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TIERS, GRACE_DAY, billingIsLocked, formatPrice, billingMonthLabel } from '../../lib/billing';

// Next-month pre-pay opens 6 days before the 1st. e.g. May billing
// becomes available on Apr 25. Mirror of the rule in athlete/Billing.js.
const NEXT_MONTH_OPEN_DAYS = 6;

function computeNextMonthOpen(now) {
  const probe = new Date(now);
  probe.setDate(now.getDate() + NEXT_MONTH_OPEN_DAYS);
  return (
    probe.getMonth() !== now.getMonth() ||
    probe.getFullYear() !== now.getFullYear()
  );
}

export function ParentBilling() {
  const { profile, refreshBilling } = useAuth();
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  // Next calendar month (handles the Dec → Jan year rollover).
  const nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const nextMonth = nextDate.getMonth() + 1;
  const nextYear = nextDate.getFullYear();
  const nextMonthOpen = computeNextMonthOpen(now);

  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  // Separate selection set for the next-month checkout. Kept apart so
  // a parent can pay current-month for athlete A and pre-pay next-
  // month for athlete B in a single Stripe session without mixing
  // the checkboxes between the two cards.
  const [selectedNext, setSelectedNext] = useState(new Set());
  const [paying, setPaying] = useState(false);
  const [payingScope, setPayingScope] = useState(null); // 'current' | 'next'

  const showSuccess = new URLSearchParams(window.location.search).get('success') === '1';

  useEffect(() => {
    if (profile) load();
  }, [profile]);

  // After Stripe redirect, poll until webhook updates paid_at
  useEffect(() => {
    if (!showSuccess || !profile) return;
    let stopped = false;
    const poll = setInterval(() => {
      if (stopped) return;
      load();
      refreshBilling();
    }, 3000);
    const timeout = setTimeout(() => { stopped = true; clearInterval(poll); }, 30000);
    return () => { stopped = true; clearInterval(poll); clearTimeout(timeout); };
  }, [showSuccess, profile]);

  async function load() {
    setLoading(true);

    const { data: links } = await supabase
      .from('parent_athlete_links')
      .select('athlete_id')
      .eq('parent_id', profile.id);

    if (!links?.length) {
      setAthletes([]);
      setLoading(false);
      return;
    }

    const ids = links.map(l => l.athlete_id);

    const queries = [
      supabase.from('profiles').select('id, full_name').in('id', ids).order('full_name'),
      supabase.from('billing').select('*').in('athlete_id', ids).eq('month', currentMonth).eq('year', currentYear),
    ];
    if (nextMonthOpen) {
      queries.push(
        supabase.from('billing').select('*').in('athlete_id', ids).eq('month', nextMonth).eq('year', nextYear)
      );
    }
    const results = await Promise.all(queries);
    const profileList = results[0].data;
    const billingRows = results[1].data;
    const nextBillingRows = nextMonthOpen ? (results[2].data ?? []) : [];

    const billingMap = {};
    (billingRows ?? []).forEach(b => { billingMap[b.athlete_id] = b; });

    const nextBillingMap = {};
    nextBillingRows.forEach(b => { nextBillingMap[b.athlete_id] = b; });

    const athleteList = (profileList ?? []).map(p => ({
      id: p.id,
      full_name: p.full_name,
      billing: billingMap[p.id] ?? null,
      nextBilling: nextBillingMap[p.id] ?? null,
    }));

    setAthletes(athleteList);

    // Pre-select athletes that have an unpaid billing record
    const defaultSelected = new Set(
      athleteList.filter(a => a.billing && !a.billing.paid_at).map(a => a.id)
    );
    setSelected(defaultSelected);
    // Don't auto-select next-month — let the parent opt in to pre-pay.
    setSelectedNext(new Set());

    setLoading(false);
  }

  function toggleSelect(athleteId) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(athleteId)) next.delete(athleteId);
      else next.add(athleteId);
      return next;
    });
  }

  function toggleSelectNext(athleteId) {
    setSelectedNext(prev => {
      const next = new Set(prev);
      if (next.has(athleteId)) next.delete(athleteId);
      else next.add(athleteId);
      return next;
    });
  }

  const payableSelected = athletes.filter(a =>
    selected.has(a.id) && a.billing && !a.billing.paid_at
  );
  const payableSelectedNext = athletes.filter(a =>
    selectedNext.has(a.id) && a.nextBilling && !a.nextBilling.paid_at
  );

  const totalToPay = payableSelected.reduce((sum, a) => sum + a.billing.amount_cents, 0);
  const totalToPayNext = payableSelectedNext.reduce((sum, a) => sum + a.nextBilling.amount_cents, 0);

  async function handleCheckout(scope = 'current') {
    const batch = scope === 'next' ? payableSelectedNext : payableSelected;
    if (!batch.length) return;
    setPaying(true);
    setPayingScope(scope);
    try {
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          billingIds: batch.map(a => (scope === 'next' ? a.nextBilling.id : a.billing.id)),
          successPath: '/parent/billing',
          cancelPath: '/parent/billing',
        }),
      });
      const { url, error } = await res.json();
      if (error) throw new Error(error);
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      setPaying(false);
      setPayingScope(null);
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ marginBottom: 24 }}>
        <p className="page-eyebrow">Account</p>
        <h1 className="page-title">Billing</h1>
      </div>

      {showSuccess && (
        <div className="billing-success-banner">
          Payment received — training access has been restored. Thank you!
        </div>
      )}

      {athletes.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-title">No linked athletes</p>
          <p className="empty-state-sub">Contact your athlete's coach to link your account.</p>
        </div>
      ) : (
        <>
          {/* ── 1. Payment Card (merged) ─────────────────────────── */}
          {(() => {
            const anyOverdue = athletes.some(a => billingIsLocked(a.billing));
            const allPaid = athletes.every(a => !!a.billing?.paid_at);
            const bgColor = allPaid ? '#f0fdf4' : anyOverdue ? '#fef2f2' : '#fffbeb';
            const borderColor = allPaid ? '#16a34a' : anyOverdue ? '#dc2626' : '#f59e0b';
            return (
              <div className="billing-tier-card" style={{ cursor: 'default', marginBottom: 24, borderColor, background: bgColor }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>{billingMonthLabel(currentMonth, currentYear)}</div>
                  {allPaid && <span className="billing-badge billing-badge--paid">All Paid</span>}
                  {anyOverdue && !allPaid && <span className="billing-badge billing-badge--overdue">Overdue</span>}
                  {!anyOverdue && !allPaid && <span className="billing-badge billing-badge--pending">Due by {GRACE_DAY}th</span>}
                </div>
                <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: '0 0 12px' }}>
                  Select athletes to pay for and check out together.
                </p>
                <div className="parent-billing-list">
                  {athletes.map(a => {
                    const b = a.billing;
                    const isPaid = !!b?.paid_at;
                    const isLocked = billingIsLocked(b);
                    const isChecked = selected.has(a.id);
                    return (
                      <div
                        key={a.id}
                        className={`parent-billing-row${isChecked && !isPaid ? ' parent-billing-row--selected' : ''}${isPaid ? ' parent-billing-row--paid' : ''}`}
                        onClick={() => !isPaid && toggleSelect(a.id)}
                        style={{ cursor: isPaid ? 'default' : 'pointer' }}
                      >
                        <div className="parent-billing-check">
                          {isPaid ? (
                            <div className="parent-billing-check-paid">✓</div>
                          ) : (
                            <input type="checkbox" checked={isChecked} onChange={() => toggleSelect(a.id)} onClick={e => e.stopPropagation()} className="parent-billing-checkbox" />
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="parent-billing-name">{a.full_name}</div>
                          <div className="parent-billing-meta">
                            {b ? `${TIERS[b.tier]?.label ?? b.tier} · ${formatPrice(b.amount_cents)}/mo` : 'No plan assigned yet'}
                          </div>
                        </div>
                        <div>
                          {isPaid ? <span className="billing-badge billing-badge--paid">Paid</span>
                            : isLocked ? <span className="billing-badge billing-badge--overdue">Overdue</span>
                            : b ? <span className="billing-badge billing-badge--pending">Due</span>
                            : <span className="billing-badge billing-badge--pending">Not assigned</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {payableSelected.length > 0 && (
                  <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 13, color: 'var(--gray-600)', fontWeight: 600 }}>
                      {payableSelected.length} athlete{payableSelected.length > 1 ? 's' : ''} · Total: <strong>{formatPrice(totalToPay)}</strong>
                    </div>
                    <button className="btn-stripe-pay" onClick={() => handleCheckout('current')} disabled={paying}>
                      {paying && payingScope === 'current' ? 'Redirecting to Stripe…' : `Pay ${formatPrice(totalToPay)} with Card`}
                    </button>
                    <p style={{ fontSize: 11, color: 'var(--gray-400)', margin: 0 }}>Secured by Stripe. Your card is never stored.</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── 1b. Next-month pre-pay card ──────────────────────────
              Mirrors the current-month card but keyed to the next
              calendar month. Opens on the 25th (or equivalent) of
              the current month; hides silently before then. */}
          {nextMonthOpen && (() => {
            const anyNextAssigned = athletes.some(a => a.nextBilling);
            const allNextPaid = anyNextAssigned && athletes.every(a => !a.nextBilling || !!a.nextBilling.paid_at);
            return (
              <div
                className="billing-tier-card"
                style={{
                  cursor: 'default',
                  marginBottom: 24,
                  borderColor: allNextPaid ? '#16a34a' : anyNextAssigned ? '#3b82f6' : 'var(--gray-200)',
                  background: allNextPaid ? '#f0fdf4' : anyNextAssigned ? '#eff6ff' : 'var(--white)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>
                    {billingMonthLabel(nextMonth, nextYear)}
                  </div>
                  {allNextPaid && <span className="billing-badge billing-badge--paid">All Paid</span>}
                  {anyNextAssigned && !allNextPaid && <span className="billing-badge billing-badge--pending">Pay early</span>}
                </div>
                <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: '0 0 12px' }}>
                  Get ahead on next month's billing — pay now so training continues uninterrupted.
                </p>
                {!anyNextAssigned ? (
                  <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: 0 }}>
                    Your athletes' next-month plans haven't been set up yet — check back soon.
                  </p>
                ) : (
                  <>
                    <div className="parent-billing-list">
                      {athletes.map(a => {
                        const b = a.nextBilling;
                        if (!b) return null;
                        const isPaid = !!b.paid_at;
                        const isChecked = selectedNext.has(a.id);
                        return (
                          <div
                            key={a.id}
                            className={`parent-billing-row${isChecked && !isPaid ? ' parent-billing-row--selected' : ''}${isPaid ? ' parent-billing-row--paid' : ''}`}
                            onClick={() => !isPaid && toggleSelectNext(a.id)}
                            style={{ cursor: isPaid ? 'default' : 'pointer' }}
                          >
                            <div className="parent-billing-check">
                              {isPaid ? (
                                <div className="parent-billing-check-paid">✓</div>
                              ) : (
                                <input type="checkbox" checked={isChecked} onChange={() => toggleSelectNext(a.id)} onClick={e => e.stopPropagation()} className="parent-billing-checkbox" />
                              )}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className="parent-billing-name">{a.full_name}</div>
                              <div className="parent-billing-meta">
                                {`${TIERS[b.tier]?.label ?? b.tier} · ${formatPrice(b.amount_cents)}/mo`}
                              </div>
                            </div>
                            <div>
                              {isPaid
                                ? <span className="billing-badge billing-badge--paid">Paid</span>
                                : <span className="billing-badge billing-badge--pending">Available</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {payableSelectedNext.length > 0 && (
                      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 13, color: 'var(--gray-600)', fontWeight: 600 }}>
                          {payableSelectedNext.length} athlete{payableSelectedNext.length > 1 ? 's' : ''} · Total: <strong>{formatPrice(totalToPayNext)}</strong>
                        </div>
                        <button className="btn-stripe-pay" onClick={() => handleCheckout('next')} disabled={paying}>
                          {paying && payingScope === 'next' ? 'Redirecting to Stripe…' : `Pay ${formatPrice(totalToPayNext)} with Card`}
                        </button>
                        <p style={{ fontSize: 11, color: 'var(--gray-400)', margin: 0 }}>Secured by Stripe. Your card is never stored.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* ── 2. Plans & Referral ───────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
            {athletes.filter(a => a.billing?.tier).map(a => {
              const tier = TIERS[a.billing.tier];
              if (!tier) return null;
              const paid = !!a.billing.paid_at;
              return (
                <div key={a.id} className={`billing-tier-card${paid ? ' billing-tier-card--paid' : ''}`} style={{ cursor: 'default' }}>
                  <div className="billing-tier-badge" style={paid ? { background: '#16a34a' } : {}}>
                    {paid ? 'Active' : 'Current Plan'}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>{a.full_name}</div>
                  <div className="billing-tier-name">{tier.label}</div>
                  <ul className="billing-tier-features">
                    {tier.features.map(f => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              );
            })}
            <div className="billing-tier-card" style={{ cursor: 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10, justifyContent: 'center' }}>
              <div style={{ fontSize: 32, lineHeight: 1 }}>🎁</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>Refer an Athlete</div>
              <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: 0, maxWidth: 260 }}>
                Know a family who'd benefit from coaching? If they sign up, you get <strong>1 month free</strong>.
              </p>
              <p style={{ fontSize: 11, color: 'var(--gray-400)', margin: 0 }}>
                Just let the coach know who you referred.
              </p>
            </div>
          </div>

          {/* ── Payment History ───────────────────────────────────── */}
          <div style={{ marginTop: 8 }}>
            {athletes.map(a => (
              <AthleteHistory
                key={a.id}
                athlete={a}
                currentMonth={currentMonth}
                currentYear={currentYear}
                hideMonth={nextMonthOpen ? nextMonth : null}
                hideYear={nextMonthOpen ? nextYear : null}
              />
            ))}
          </div>
        </>
      )}
    </Layout>
  );
}

function AthleteHistory({ athlete, currentMonth, currentYear, hideMonth, hideYear }) {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    supabase
      .from('billing')
      .select('*')
      .eq('athlete_id', athlete.id)
      .order('year', { ascending: false })
      .order('month', { ascending: false })
      .limit(6)
      .then(({ data }) => {
        // Exclude the current-month row (shown in the top card) and,
        // when the next-month window is open, also the next-month row
        // (shown in the pre-pay card) so history doesn't duplicate.
        setHistory(
          (data ?? []).filter(r =>
            !(r.month === currentMonth && r.year === currentYear) &&
            !(hideMonth != null && r.month === hideMonth && r.year === hideYear)
          )
        );
      });
  }, [athlete.id, hideMonth, hideYear]);

  if (!history.length) return null;

  return (
    <div className="section-card" style={{ marginBottom: 20 }}>
      <div className="section-card-header">
        <span className="section-card-title">{athlete.full_name} — History</span>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Month</th>
            <th>Plan</th>
            <th>Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {history.map(row => (
            <tr key={row.id}>
              <td>{billingMonthLabel(row.month, row.year)}</td>
              <td>{TIERS[row.tier]?.label ?? row.tier}</td>
              <td>
                {formatPrice(row.amount_cents)}
                {row.amount_cents === 0 && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: '#16a34a', fontWeight: 600 }}>Credit Applied</span>
                )}
              </td>
              <td>
                {row.paid_at
                  ? <span className="billing-badge billing-badge--paid">Paid</span>
                  : <span className="billing-badge billing-badge--overdue">Unpaid</span>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
